import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spin } from 'antd';
import { ICustomer } from '../../../../../../api/Customer_V2/customerApi_V2';
import { noData } from '../../../../../../components/ExportImgFigma';
import { NO_DATA } from '../../../../../../env';
import { FlattenedField } from '../../../../../CustomersV2/indexV2';

// ── Types ─────────────────────────────────────────────────────────────────────

export type VoucherColumn = {
    id: string;       // fieldCode
    label: string;    // fieldLabel
    width?: number;
    minWidth?: number;
    render: (row: ICustomer) => React.ReactNode;
};

type VoucherTreeRow = ICustomer & { children?: ICustomer[] };

// ── Helpers ───────────────────────────────────────────────────────────────────

const getFieldValue = (row: ICustomer, fieldCode: string, fallback = '') =>
    row.fields.find(f => f.fieldCode === fieldCode)?.value ?? fallback;

const buildVoucherTree = (items: ICustomer[]): VoucherTreeRow[] => {
    const groupMap = new Map<number, ICustomer[]>();
    items.forEach(item => {
        const group = groupMap.get(item.id) ?? [];
        group.push(item);
        groupMap.set(item.id, group);
    });
    return Array.from(groupMap.values()).map(group => {
        const parent = group.find(item => item.conversionId === null) ?? group[0];
        const children = group.filter(item => item !== parent && item.conversionId !== null);
        return { ...parent, children };
    });
};

/** Build VoucherColumn list from FlattenedField[], filtered by visibleFieldCodes.
 *  Cột 'code' (Mã voucher) luôn được thêm vào đầu tiên — đây là cột chứa tree toggle.
 */
export const buildColumnsFromFields = (
    idCustomer: number | undefined,
    fields: FlattenedField[],
    visibleFieldCodes: string[],
): VoucherColumn[] => {
    const codeColumn: VoucherColumn = {
        id: 'code',
        label: 'Mã voucher',
        width: 260,
        render: (row: ICustomer) => row.code ?? NO_DATA,
    };

    // const dynamicColumns = fields
    //     .filter(f => visibleFieldCodes.includes(f.fieldCode))
    //     .map(f => ({
    //         id: f.fieldCode,
    //         label: f.fieldLabel,
    //         width: 200,
    //         render: (row: ICustomer) => getFieldValue(row, f.fieldCode, 'Không xác định'),
    //     }));

    // return [codeColumn, ...dynamicColumns];

    const dynamicColumns = fields
        .filter(f => visibleFieldCodes.includes(f.fieldCode))
        .map(f => ({
            id: f.fieldCode,
            label: f.fieldLabel,
            width: 200,
            render: (row: ICustomer) => {
                const value = getFieldValue(row, f.fieldCode, NO_DATA);

                if (
                    f.fieldCode === 'name_campaign' && row.campaignId
                ) {
                    return (
                        <Link
                            to={`/marketing/campaign-sends-notifications/access`}
                            state={{
                                firstPage: '/customer/list/edit',
                                idCustomer,
                                id: row.campaignId,
                                action: 'access',
                                isAutoCampaign: row.isAutoCampaign
                            }}
                            className="campaign-title-link"
                            onClick={(event) => event.stopPropagation()}
                            style={{
                                color: '#096DD9'
                            }}
                        >
                            {value}
                        </Link>
                    );
                }
                if (
                    f.fieldCode === 'name_voucher' && row.idVoucher
                ) {
                    return (
                        <Link
                            to={`/marketing/vouchers-management/access`}
                            state={{
                                firstPage: '/customer/list/edit',
                                idCustomer,
                                id: row.idVoucher,
                            }}
                            className="campaign-title-link"
                            onClick={(event) => event.stopPropagation()}
                            style={{
                                color: '#096DD9'
                            }}
                        >
                            {value}
                        </Link>
                    );
                }

                if (
                    f.fieldCode === 'status_voucher'
                ) {

                    const dataColor = row.fields.find(x => x.fieldCode === 'status_voucher')?.additionalData
                    return (
                        <div
                            className="custom-page-css flex items-center justify-center rounded w-fit  "
                            style={{
                                padding: '4px 8px',
                                border: `1px solid ${dataColor?.colors?.border}`,
                                color: `${dataColor?.colors?.text}`,
                                background: `${dataColor?.colors?.bg}`,
                            }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {value}
                        </div>
                    );
                }

                return value;
            },
        }));
    return [codeColumn, ...dynamicColumns]
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
    data: ICustomer[];
    /** All available columns (already filtered to visible ones) */
    columns: VoucherColumn[];
    loading?: boolean;
    idCustomer: number | undefined;
}

// ── Component ─────────────────────────────────────────────────────────────────

const VoucherVersionTable: React.FC<Props> = ({ data, columns: columnsProp, loading, idCustomer }) => {
    const [expandedIds, setExpandedIds] = useState<number[]>([]);

    // ── Resize ────────────────────────────────────────────────────────────────
    const resizingRef = useRef<{
        columnId: string;
        startX: number;
        startWidth: number;
    } | null>(null);

    // ── Drag-reorder ──────────────────────────────────────────────────────────
    const dragSourceRef = useRef<string | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);

    const rows = useMemo(() => buildVoucherTree(data), [data]);

    // Column order — reset when columnsProp changes (e.g. after show/hide)
    const [columnOrder, setColumnOrder] = useState<string[]>(() =>
        columnsProp.map(c => c.id),
    );

    // Sync order when visible columns change from outside (setting popup)
    const prevColumnIds = useRef<string>(columnsProp.map(c => c.id).join(','));
    const newColumnIds = columnsProp.map(c => c.id).join(',');
    if (prevColumnIds.current !== newColumnIds) {
        prevColumnIds.current = newColumnIds;
        // Thứ tự từ ngoài là chuẩn (đã được kéo/thả trong popup cấu hình)
        setColumnOrder(columnsProp.map(c => c.id));
    }
    const columns = useMemo<VoucherColumn[]>(() => {
        return columnOrder
            .map(id => columnsProp.find(c => c.id === id))
            .filter(Boolean) as VoucherColumn[];
    }, [columnOrder, columnsProp]);

    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

    const getWidth = (col: VoucherColumn) =>
        columnWidths[col.id] ?? col.width ?? 200;

    // ── Tree expand ───────────────────────────────────────────────────────────
    const toggleExpand = (rowId: number) => {
        setExpandedIds(prev =>
            prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId],
        );
    };
    const isExpanded = (rowId: number) => expandedIds.includes(rowId);

    // ── Resize handlers ───────────────────────────────────────────────────────
    const handleResizeStart = (
        e: React.MouseEvent<HTMLDivElement>,
        col: VoucherColumn,
    ) => {
        e.preventDefault();
        e.stopPropagation();
        resizingRef.current = {
            columnId: col.id,
            startX: e.clientX,
            startWidth: getWidth(col),
        };
        document.addEventListener('mousemove', handleResizeMove);
        document.addEventListener('mouseup', handleResizeEnd);
    };

    const handleResizeMove = (e: MouseEvent) => {
        const r = resizingRef.current;
        if (!r) return;
        // min 40px — chỉ cần đủ để handle còn thao tác được
        const next = Math.max(40, r.startWidth + (e.clientX - r.startX));
        setColumnWidths(prev => ({ ...prev, [r.columnId]: next }));
    };

    const handleResizeEnd = () => {
        resizingRef.current = null;
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
    };

    // ── Drag-reorder handlers ─────────────────────────────────────────────────
    const FIXED_COL = 'code'; // cột này luôn cố định bên trái, không kéo/thả được

    const handleDragStart = (e: React.DragEvent<HTMLTableCellElement>, id: string) => {
        if (resizingRef.current || id === FIXED_COL) { e.preventDefault(); return; }
        dragSourceRef.current = id;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableCellElement>, id: string) => {
        // Không cho drop vào vị trí cột cố định
        if (id === FIXED_COL) { e.preventDefault(); return; }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragSourceRef.current && dragSourceRef.current !== id) setDragOverId(id);
    };

    const handleDragLeave = () => setDragOverId(null);

    const handleDrop = (e: React.DragEvent<HTMLTableCellElement>, targetId: string) => {
        e.preventDefault();
        // Không cho drop vào cột cố định
        if (targetId === FIXED_COL) { setDragOverId(null); return; }
        const sourceId = dragSourceRef.current;
        if (!sourceId || sourceId === targetId) { setDragOverId(null); return; }
        setColumnOrder(prev => {
            const next = [...prev];
            const from = next.indexOf(sourceId);
            const to = next.indexOf(targetId);
            // Không cho cột nào nhảy lên trước cột cố định (index 0)
            if (to === 0) { return prev; }
            next.splice(from, 1);
            next.splice(to, 0, sourceId);
            return next;
        });
        dragSourceRef.current = null;
        setDragOverId(null);
    };

    const handleDragEnd = () => {
        dragSourceRef.current = null;
        setDragOverId(null);
    };

    // ── Cell renderer ─────────────────────────────────────────────────────────
    const renderCell = (
        row: ICustomer,
        col: VoucherColumn,
        opts?: { hasChildren?: boolean; expanded?: boolean; isChild?: boolean; onToggle?: () => void },
    ) => {
        // 'code' column always gets the tree toggle
        const isFirst = col.id === 'code';
        if (!isFirst) return col.render(row);

        return (
            <div className={`voucher-code-cell ${opts?.isChild ? 'is-child' : ''}`}>
                {opts?.hasChildren ? (
                    <button
                        type="button"
                        className={`tree-toggle ${opts.expanded ? 'is-open' : ''}`}
                        onClick={e => { e.stopPropagation(); opts.onToggle?.(); }}
                    >
                        <span />
                    </button>
                ) : (
                    <span className="tree-toggle-placeholder" />
                )}
                <span className="voucher-code-text">{col.render(row)}</span>
            </div>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────
    if (!columns.length) {
        return (
            <div className="voucher-table-wrapper" style={{ minHeight: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                Không có cột nào được hiển thị
            </div>
        );
    }

    return (
        <Spin spinning={loading}>
            <div className="voucher-table-wrapper"
                style={{ minHeight: rows.length === 0 ? 250 : undefined }}
            >
                <table className="voucher-table" style={{
                    width: columns.reduce((sum, col) => sum + getWidth(col), 0),
                    position: 'relative',


                }}>
                    <colgroup>
                        {columns.map(col => (
                            <col key={col.id} style={{ width: getWidth(col), minWidth: 0 }} />
                        ))}
                    </colgroup>

                    <thead>
                        <tr>
                            {columns.map((col, i) => {
                                const isFixed = col.id === FIXED_COL;
                                return (
                                    <th
                                        key={col.id}
                                        className={[
                                            'dt-cell dt-th bg-096DD9 text-white h-45px',
                                            isFixed ? 'col-fixed' : '',
                                            dragOverId === col.id ? 'col-drag-over' : '',
                                            dragSourceRef.current === col.id ? 'col-dragging' : '',
                                        ].filter(Boolean).join(' ')}
                                        style={{
                                            width: getWidth(col),
                                            maxWidth: getWidth(col),
                                            minWidth: 0,
                                            overflow: 'hidden',
                                        }}
                                        draggable={!isFixed}
                                        onDragStart={e => handleDragStart(e, col.id)}
                                        onDragOver={e => handleDragOver(e, col.id)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={e => handleDrop(e, col.id)}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <div className="dt-th-inner">
                                            <span className="dt-th-text text-left text-white">{col.label}</span>
                                        </div>
                                        <div
                                            className={`resize-handle ${columns.length === i + 1 ? 'd-none' : ''}`}
                                            onMouseDown={e => handleResizeStart(e, col)}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody style={{
                    }}>
                        {rows.length > 0
                            ?
                            rows.map((row, i) => {
                                const hasChildren = !!row.children?.length;
                                const expanded = isExpanded(row.id);
                                return (
                                    <React.Fragment key={`${row.id}-${row.conversionId ?? 'root'}`}>
                                        <tr className="voucher-row voucher-parent-row"
                                            // style={{
                                            //     backgroundColor: i % 2 === 0 ? '#ffffff' : '#f7f9fc',
                                            // }}
                                        >
                                            {columns.map(col => {
                                                const isFixed = col.id === FIXED_COL;
                                                return (
                                                    <td key={col.id} style={{
                                                        width: getWidth(col), maxWidth: getWidth(col), minWidth: 0, overflow: 'hidden',
                                                    }}>
                                                        {renderCell(row, col, {
                                                            hasChildren,
                                                            expanded,
                                                            onToggle: () => toggleExpand(row.id),
                                                        })}
                                                    </td>
                                                );
                                            })}
                                        </tr>

                                        {expanded && row.children?.map(child => (
                                            <tr
                                                key={`${child.id}-${child.conversionId}`}
                                                className="voucher-row voucher-child-row"
                                            >
                                                {columns.map(col => {
                                                    const isFixed = col.id === FIXED_COL;
                                                    return (
                                                        <td key={col.id} style={{
                                                            width: getWidth(col), maxWidth: getWidth(col), minWidth: 0, overflow: 'hidden',
                                                        }}>
                                                            {renderCell(child, col, { isChild: true })}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })
                            :
                            null
                        }
                    </tbody>
                </table>
                {rows.length === 0 && (
                    // Sticky left + width theo viewport của scroll container => luôn ở giữa vùng nhìn thấy khi scroll ngang
                    <div
                        className="dt-empty"
                        style={{
                            position: 'sticky',
                            left: 0,
                            width: '100%',
                            maxWidth: '100%',
                            height: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                        }}
                    >
                        <img src={noData} alt="No data" style={{ maxWidth: 150 }} />
                        <div>Không có dữ liệu</div>
                    </div>
                )}
            </div>
        </Spin>
    );
};

export default VoucherVersionTable;
